// frontend/src/components/Email/EmailComposeModal.jsx
// Modal de composição de email com rascunho gerado por IA.
// Props: { isOpen, onClose, contact, onSendSuccess? }
// contact deve ter: id, email, first_name, last_name
// onSendSuccess: callback opcional chamado após envio bem-sucedido
//   (use para invalidar react-query caches no componente pai)
import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CC_ADDRESS = 'comercial@wacontactcenter.com.br';

const EmailComposeModal = ({ isOpen, onClose, contact, onSendSuccess }) => {
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftFailed, setDraftFailed] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // Track whether we've already requested a draft for this open session
  const draftRequestedRef = useRef(false);

  // Reset state and request a fresh draft each time the modal opens with a valid contact
  useEffect(() => {
    if (!isOpen) {
      // Reset everything on close so next open gets a fresh draft
      setDraftLoading(false);
      setDraftFailed(false);
      setSubject('');
      setBody('');
      setSending(false);
      draftRequestedRef.current = false;
      return;
    }

    if (!contact?.email) return;
    if (draftRequestedRef.current) return;

    draftRequestedRef.current = true;
    const fetchDraft = async () => {
      setDraftLoading(true);
      setDraftFailed(false);
      try {
        // Timeout propio: el flow n8n (Gemini) tarda ~14-17s y el default del
        // cliente es 10s — sin esto el browser aborta antes de que el backend
        // (timeout 35s) responda y el rascunho "falla" siempre.
        const res = await api.post(
          '/email/actuator/draft',
          { contactId: contact.id },
          { timeout: 40000 }
        );
        if (res.data.success) {
          const { subject: s, body: b, draft_failed } = res.data.data;
          if (draft_failed) {
            setDraftFailed(true);
            setSubject('');
            setBody('');
          } else {
            setSubject(s || '');
            setBody(b || '');
          }
        } else {
          setDraftFailed(true);
        }
      } catch {
        setDraftFailed(true);
      } finally {
        setDraftLoading(false);
      }
    };

    fetchDraft();
  }, [isOpen, contact]);

  if (!isOpen) return null;

  // No email on contact
  if (!contact?.email) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Enviar Email
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
            Sem email cadastrado.
          </p>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      await api.post('/email/actuator/send', {
        contactId: contact.id,
        to: contact.email,
        subject: subject.trim(),
        body: body.trim(),
      }, { timeout: 30000 });
      toast.success('Email enviado!');
      // Notify parent so it can invalidate react-query caches
      onSendSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao enviar email. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  const isSendDisabled = sending || draftLoading || !subject.trim() || !body.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Enviar Email
          </h3>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* To */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Para
            </label>
            <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
              {contact.email}
            </p>
          </div>

          {/* CC */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              CC
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
              {CC_ADDRESS}
            </p>
          </div>

          {/* Amber notice when draft failed */}
          {draftFailed && !draftLoading && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
              <span className="text-amber-600 text-xs">
                Rascunho IA indisponivel — escreva manualmente.
              </span>
            </div>
          )}

          {/* Loading state */}
          {draftLoading ? (
            <div className="flex items-center justify-center py-12 space-x-3">
              <svg
                className="animate-spin h-5 w-5 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Gerando rascunho com IA…
              </span>
            </div>
          ) : (
            <>
              {/* Subject */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
                  Assunto
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Assunto do email…"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
                  Mensagem
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 resize-y"
                  placeholder="Corpo do email…"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={isSendDisabled}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Enviando…' : 'Enviar Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposeModal;
